import { lazy, Suspense, useRef } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { RequireAuth, useAuth } from "./lib/auth";
import { ChatProvider } from "./lib/chatContext";
import { TimerProvider } from "./lib/timerContext";
import { useSwipeBack } from "./lib/useSwipeBack";
import { TourProvider } from "./lib/useTour";
import { Tour } from "./components/tour/Tour";
import { MealReminders } from "./components/MealReminders";
import { Onboarding } from "./pages/Onboarding";
import { Cookbook } from "./pages/Cookbook";
// A shared link is a cold front door: someone arrives here with no app loaded
// and no account. Making them wait on a second chunk after the first is
// exactly backwards for the one page strangers see, so it stays eager.
import { PublicRecipe } from "./pages/PublicRecipe";

// Everything above is on the path to a first screen: you land on the cookbook
// or you land on the login page. Everything below is somewhere you navigate to
// afterwards, so it has no business being in the bundle you wait for on a cold
// open — the whole app used to ship as one ~1 MB file that had to download,
// parse and compile before the shelves could appear.
//
// Each of these becomes its own chunk, fetched the first time you go there and
// cached from then on.
const OnDeck = lazy(() => import("./pages/OnDeck").then((m) => ({ default: m.OnDeck })));
const Shopping = lazy(() => import("./pages/Shopping").then((m) => ({ default: m.Shopping })));
const Preferences = lazy(() =>
  import("./pages/Preferences").then((m) => ({ default: m.Preferences }))
);
const RecipePage = lazy(() => import("./pages/Recipe").then((m) => ({ default: m.RecipePage })));
const Cook = lazy(() => import("./pages/Cook").then((m) => ({ default: m.Cook })));
const NewRecipe = lazy(() => import("./pages/NewRecipe").then((m) => ({ default: m.NewRecipe })));
const Revisions = lazy(() => import("./pages/Revisions").then((m) => ({ default: m.Revisions })));
const KitchenTable = lazy(() =>
  import("./pages/KitchenTable").then((m) => ({ default: m.KitchenTable }))
);
const CommunityRecipe = lazy(() =>
  import("./pages/CommunityRecipe").then((m) => ({ default: m.CommunityRecipe }))
);
const ThePass = lazy(() => import("./pages/ThePass").then((m) => ({ default: m.ThePass })));
const Assistant = lazy(() => import("./pages/Assistant").then((m) => ({ default: m.Assistant })));
// Mounted on every screen but opened on almost none, and it drags the whole
// chat stack in with it.
const ChatOverlay = lazy(() =>
  import("./components/chat/ChatOverlay").then((m) => ({ default: m.ChatOverlay }))
);
// Plays once, then only on request — no reason for its markup, styles or the
// SVG spine to sit in the bundle everyone downloads on every visit.
const Intro = lazy(() => import("./components/intro/Intro").then((m) => ({ default: m.Intro })));

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return <Onboarding />;
}

export function App() {
  // A swipe in from the left edge goes back a page: the web view has no
  // browser chrome and iOS's own edge gesture stops at its door.
  const shellRef = useRef<HTMLDivElement>(null);
  useSwipeBack(shellRef);

  return (
    <ChatProvider>
      <TimerProvider>
      <TourProvider>
      <div className="shell" ref={shellRef}>
        <main>
          {/* One boundary around the table rather than per route: a chunk
              fetches in a few hundred milliseconds on any real connection, and
              a spinner that appears for each navigation is worse than a brief
              blank on the first visit to a page. */}
          <Suspense fallback={<div className="empty"><span className="spinner" /></div>}>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            {/* Public read-only links — viewable without signing in. */}
            <Route path="/r/:shareId" element={<PublicRecipe />} />
            <Route path="/r/:shareId/v/:rev" element={<PublicRecipe />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <Cookbook />
                </RequireAuth>
              }
            />
            <Route
              path="/recipe/new"
              element={
                <RequireAuth>
                  <NewRecipe />
                </RequireAuth>
              }
            />
            <Route
              path="/recipe/:id"
              element={
                <RequireAuth>
                  <RecipePage />
                </RequireAuth>
              }
            />
            {/* Cooking mode — phone only; the page itself redirects a desktop
                viewport back to the recipe. */}
            <Route
              path="/recipe/:id/cook"
              element={
                <RequireAuth>
                  <Cook />
                </RequireAuth>
              }
            />
            <Route
              path="/recipe/:id/revisions"
              element={
                <RequireAuth>
                  <Revisions />
                </RequireAuth>
              }
            />
            <Route
              path="/on-deck"
              element={
                <RequireAuth>
                  <OnDeck />
                </RequireAuth>
              }
            />
            <Route
              path="/shopping"
              element={
                <RequireAuth>
                  <Shopping />
                </RequireAuth>
              }
            />
            <Route
              path="/community"
              element={
                <RequireAuth>
                  <KitchenTable />
                </RequireAuth>
              }
            />
            <Route
              path="/community/:shareId"
              element={
                <RequireAuth>
                  <CommunityRecipe />
                </RequireAuth>
              }
            />
            <Route
              path="/served"
              element={
                <RequireAuth>
                  <ThePass />
                </RequireAuth>
              }
            />
            <Route
              path="/preferences"
              element={
                <RequireAuth>
                  <Preferences />
                </RequireAuth>
              }
            />
            <Route
              path="/connect"
              element={
                <RequireAuth>
                  <Assistant />
                </RequireAuth>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </main>
      </div>
      {/* No fallback: the overlay is invisible until opened, so there is
          nothing to show while its chunk loads. */}
      <Suspense fallback={null}>
        <ChatOverlay />
      </Suspense>
      <Suspense fallback={null}>
        <Intro />
      </Suspense>
      <MealReminders />
      <Tour />
      </TourProvider>
      </TimerProvider>
    </ChatProvider>
  );
}
